import { useState, useEffect, useRef, useCallback } from "react";
import { SupabaseClient, RealtimeChannel } from "@supabase/supabase-js";
import { throttle } from "lodash";

export interface UseChatTypingProps {
  roomId: string;
  userId: string;
  userName: string;
  supabaseClient: SupabaseClient;
  typingTimeoutMs?: number;
}

export interface TypingUser {
  user_id: string;
  user_name: string;
  typing: boolean;
  online_at: string;
}

/**
 * Presence-based Typing Indicator Hook (Isolated Channel)
 *
 * Uses a dedicated Presence channel ('typing:room:{id}') to avoid conflicts
 * with the main chat channel. This mirrors the successful pattern used in
 * usePresenceStatus hook.
 */
export function useChatTyping({
  roomId,
  userId,
  userName,
  supabaseClient,
  typingTimeoutMs = 3000,
}: UseChatTypingProps) {
  const [isTyping, setIsTyping] = useState(false);
  const [typingUsers, setTypingUsers] = useState<TypingUser[]>([]);
  const stopTypingTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(
    null,
  );
  const channelRef = useRef<RealtimeChannel | null>(null);

  // 1. Setup & Subscribe to Dedicated Presence Channel
  useEffect(() => {
    if (!roomId || !userId || !supabaseClient) return;

    const channelName = `typing:room:${roomId}`;
    console.log("[useChatTyping] Connecting to channel:", channelName);

    const channel = supabaseClient.channel(channelName, {
      config: {
        presence: {
          key: userId,
        },
      },
    });

    const updateTypingUsers = () => {
      const state = channel.presenceState<{
        user_id: string;
        user_name: string;
        typing: boolean;
        online_at: string;
      }>();
      const users: TypingUser[] = [];
      const now = Date.now();

      Object.values(state).forEach((presences) => {
        presences.forEach((presence) => {
          if (presence.user_id !== userId && presence.typing) {
            // Check for stale events (optional safety)
            const lastActive = new Date(presence.online_at).getTime();
            if (now - lastActive < typingTimeoutMs + 5000) {
              users.push(presence as TypingUser);
            }
          }
        });
      });

      console.log("[useChatTyping] Users updated:", users);
      setTypingUsers(users);
    };

    channel
      .on("presence", { event: "sync" }, () => {
        console.log("[useChatTyping] SYNC received");
        updateTypingUsers();
      })
      .on("presence", { event: "join" }, ({ key, newPresences }) => {
        console.log("[useChatTyping] JOIN:", key, newPresences);
        updateTypingUsers();
      })
      .on("presence", { event: "leave" }, ({ key, leftPresences }) => {
        console.log("[useChatTyping] LEAVE:", key, leftPresences);
        updateTypingUsers();
      })
      .subscribe(async (status) => {
        console.log("[useChatTyping] Status:", status);
        if (status === "SUBSCRIBED") {
          // Track initial state (not typing) to register presence
          // This is important so 'leave' events work if they disconnect
          await channel.track({
            user_id: userId,
            user_name: userName,
            typing: false,
            online_at: new Date().toISOString(),
          });
        }
      });

    channelRef.current = channel;

    return () => {
      console.log("[useChatTyping] Cleaning up channel");
      supabaseClient.removeChannel(channel);
      channelRef.current = null;
    };
  }, [roomId, userId, userName, supabaseClient, typingTimeoutMs]);

  // 2. Track Logic with Throttling
  const sendTypingState = useCallback(
    async (isTypingState: boolean) => {
      const activeChannel = channelRef.current;
      if (!activeChannel) return;

      try {
        console.log("[useChatTyping] Tracking:", isTypingState);
        await activeChannel.track({
          user_id: userId,
          user_name: userName,
          typing: isTypingState,
          online_at: new Date().toISOString(),
        });
      } catch (err) {
        console.error("[useChatTyping] Track error:", err);
      }
    },
    [userId, userName],
  );

  // Throttled function
  const throttledTrackRef = useRef(
    throttle((_val: boolean) => sendTypingState(true), 2000, {
      trailing: false,
    }),
  );

  const cancelTyping = useCallback(() => {
    if (stopTypingTimeoutRef.current) {
      clearTimeout(stopTypingTimeoutRef.current);
    }
    setIsTyping(false);
    sendTypingState(false);
  }, [sendTypingState]);

  const handleTyping = useCallback(() => {
    // 1. Local state update
    if (!isTyping) {
      setIsTyping(true);
      sendTypingState(true); // Send immediately
    } else {
      throttledTrackRef.current(true); // Throttle
    }

    // 2. Schedule stop
    if (stopTypingTimeoutRef.current) {
      clearTimeout(stopTypingTimeoutRef.current);
    }

    stopTypingTimeoutRef.current = setTimeout(() => {
      cancelTyping();
    }, typingTimeoutMs);
  }, [isTyping, typingTimeoutMs, sendTypingState, cancelTyping]);

  // Cleanup throttle on unmount
  useEffect(() => {
    return () => {
      throttledTrackRef.current.cancel();
    };
  }, []);

  return {
    isTyping,
    typingUsers,
    handleTyping,
    cancelTyping,
  };
}
